import type { VirtualItem } from '@tanstack/react-virtual';
import { ConfigProvider } from 'lib/antd';
import type { ChatRoom, Message } from 'models';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Icon, InfinityLoading } from 'ui';
import { useSendPendingChatMessageUseEffect } from '../../hooks';
import type { PendingMessage } from '../../types';
import {
  useChatMessagesVirtualizer,
  useMarkChatroomAsReadUseEffect,
  useMessageGroups,
  useScrollToLastSentMessageEffect,
  useVirtualChatRows,
} from './hooks';
import { VirtualChatRowItem } from './virtual-chat-row-item';

type P = {
  chatroom: ChatRoom;
  fetchNextPage: () => unknown;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  messageList: Message[];
  onPendingMessageConsumed: () => void;
  pendingMessage?: PendingMessage;
};

const ChatMessagesList = ({
  chatroom,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  messageList,
  onPendingMessageConsumed,
  pendingMessage,
}: P) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const messageGroups = useMessageGroups(messageList, chatroom);
  const rows = useVirtualChatRows({
    hasNextPage,
    messageGroups,
  });

  const { isAtEnd, scrollElementRef, virtualizer } = useChatMessagesVirtualizer({
    rows,
  });

  useScrollToLastSentMessageEffect({
    rows,
    virtualizer,
  });

  const unreadCount = chatroom.stats?.unread ?? 0;
  const unseenCount = chatroom.stats?.unseen ?? 0;

  useMarkChatroomAsReadUseEffect({
    chatroomId: chatroom.id,
    isAtEnd,
    unreadCount,
    unseenCount,
  });

  useSendPendingChatMessageUseEffect({
    chatroomId: chatroom.id,
    onPendingMessageConsumed,
    pendingMessage,
  });

  return (
    <ConfigProvider getPopupContainer={() => wrapperRef.current ?? document.body}>
      <div className="chat-messages-list-wrapper" ref={wrapperRef}>
        <div className="chat-messages-list" ref={scrollElementRef}>
          <InfinityLoading hasMoreData={hasNextPage} isLoading={isFetchingNextPage} onEnter={fetchNextPage} />
          <div className="chat-messages-list__items" style={{ height: `${virtualizer.getTotalSize()}px` }}>
            {virtualizer.getVirtualItems().map((virtualItem: VirtualItem) => {
              const row = rows[virtualItem.index];

              if (!row) {
                return null;
              }

              const isDayDivider = row.kind === 'day-divider';
              const isFirstMessageInGroup = row.kind === 'message' && row.group.index === 0;

              return (
                <VirtualChatRowItem
                  key={virtualItem.key}
                  paddingBottom={isDayDivider}
                  paddingTop={isFirstMessageInGroup || isDayDivider}
                  row={row}
                  virtualItem={virtualItem}
                  virtualizer={virtualizer}
                />
              );
            })}
          </div>
        </div>
        {unseenCount > 0 && !isAtEnd ? (
          <div className="chat-messages-list-wrapper__button">
            <Button
              icon={<Icon icon="arrow-down-line" />}
              onClick={() => {
                virtualizer.scrollToEnd();
              }}
              type="primary"
            >
              {t('chatNewIncomingMessages', { count: unseenCount })}
            </Button>
          </div>
        ) : null}
      </div>
    </ConfigProvider>
  );
};

export default ChatMessagesList;
